/**
 * Deleting a lot or a farm removes it from the server (a lot is
 * soft-deleted; a farm is hard-deleted and cascades through Prisma FK
 * constraints to every Lot/FeedLog/HarvestLog/LotCostEvent/WaterLog/
 * MortalityLog/WeightSampleLog/FishStockLog underneath it — see
 * docs/architecture-concerns.md for the fuller writeup). Neither delete
 * path cleans up the corresponding LOCAL Dexie cache, though: the six
 * event-log repos are only ever `upsert`ed, never reconciled against the
 * server (unlike `farms`/`lots`, which get a full `replaceAll` on sync),
 * so their rows for a deleted lot/farm linger forever, orphaned. That
 * surfaced as unlabeled "ghost" lot values in Reports (see
 * decorateRowsWithRemovedFarmLotLabel in reportCellDisplay.ts, the
 * display-side symptom fix).
 *
 * These two functions are the actual fix: called from every delete path
 * (Lots screen, EditFarm's farm delete, EditFarm's PU delete) to purge
 * the matching local rows at the moment of deletion, closing the leak at
 * its source rather than only patching how it's displayed downstream.
 *
 * Best-effort by design (mirrors deleteProductionUnitTableDataset's own
 * "best-effort: also delete Dexie-backed rows" convention): this only
 * ever removes local copies of data the server has already agreed is
 * gone (the delete request is already queued or sent), so a purge
 * failure just means stale data lingers a bit longer — never a new
 * correctness risk. Never let a purge failure block the delete itself.
 */
import { ensureOfflineReady } from '../offline/initOffline'
import { dexieLotRepo } from '../offline/repos/DexieLotRepo'
import { dexieLotEventRepo } from '../offline/repos/DexieLotEventRepo'
import { dexieFeedLogRepo } from '../offline/repos/DexieFeedLogRepo'
import { dexieHarvestLogRepo } from '../offline/repos/DexieHarvestLogRepo'
import { dexieLotCostEventRepo } from '../offline/repos/DexieLotCostEventRepo'
import { dexieMortalityLogRepo } from '../offline/repos/DexieMortalityLogRepo'
import { dexieWeightSampleLogRepo } from '../offline/repos/DexieWeightSampleLogRepo'
import { dexieFishStockLogRepo } from '../offline/repos/DexieFishStockLogRepo'
import { dexieProductionTableRepo } from '../offline/repos/DexieProductionTableRepo'
import { deleteProductionUnitTableDataset } from './productionUnitTable'
import { deleteWaterTableRows } from './waterTable'

/** Purges every locally-cached event-log row for one lot. Safe to call
 * whether or not those rows exist — each repo's `deleteMany` no-ops on
 * an empty id list. */
export const purgeLocalDataForLot = async (lotId: string): Promise<void> => {
  const trimmed = lotId.trim()
  if (!trimmed) return

  try {
    await ensureOfflineReady()

    const [feedLogs, harvestLogs, costEvents, mortalityLogs, weightSampleLogs, fishStockLogs] = await Promise.all([
      dexieFeedLogRepo.list({ lotId: trimmed }),
      dexieHarvestLogRepo.list({ lotId: trimmed }),
      dexieLotCostEventRepo.list({ lotId: trimmed }),
      dexieMortalityLogRepo.list({ lotId: trimmed }),
      dexieWeightSampleLogRepo.list({ lotId: trimmed }),
      dexieFishStockLogRepo.list({ lotId: trimmed })
    ])

    await Promise.all([
      dexieFeedLogRepo.deleteMany(feedLogs.map((row) => row.id)),
      dexieHarvestLogRepo.deleteMany(harvestLogs.map((row) => row.id)),
      dexieLotCostEventRepo.deleteMany(costEvents.map((row) => row.id)),
      dexieMortalityLogRepo.deleteMany(mortalityLogs.map((row) => row.id)),
      dexieWeightSampleLogRepo.deleteMany(weightSampleLogs.map((row) => row.id)),
      dexieFishStockLogRepo.deleteMany(fishStockLogs.map((row) => row.id)),
      // Without this, the lot's productionTableRows entry survives (it's
      // not one of the six event logs above), so Reports/Datatable/Panel
      // still show a row for the deleted lot — just with every
      // overlay-computed value (SGR/FCR/SFR, weight, etc.) blank now that
      // its source events are gone, which reads as "broken", not "gone".
      dexieProductionTableRepo.deleteAllForLotId(trimmed)
    ])

    await dexieLotEventRepo.pruneUnreachable(await dexieLotRepo.list())
  } catch (error) {
    console.warn('[entityCachePurge] failed to purge local lot data', error)
  }
}

/** Purges every locally-cached lot and event-log row for one production
 * unit — the same gap as `purgeLocalDataForFarm`, one level down (today
 * `handleDeletePU` only cleans production-table/water rows, not the six
 * event-log repos or the PU's own lots). */
export const purgeLocalDataForProductionUnit = async (
  farmId: string,
  productionUnitId: string
): Promise<void> => {
  const trimmedFarmId = farmId.trim()
  const trimmedPuId = productionUnitId.trim()
  if (!trimmedFarmId || !trimmedPuId) return

  try {
    await ensureOfflineReady()

    const [feedLogs, harvestLogs, costEvents, mortalityLogs, weightSampleLogs, fishStockLogs, lots] = await Promise.all([
      dexieFeedLogRepo.list({ farmId: trimmedFarmId, productionUnitId: trimmedPuId }),
      dexieHarvestLogRepo.list({ farmId: trimmedFarmId, productionUnitId: trimmedPuId }),
      dexieLotCostEventRepo.list({ farmId: trimmedFarmId, productionUnitId: trimmedPuId }),
      dexieMortalityLogRepo.list({ farmId: trimmedFarmId, productionUnitId: trimmedPuId }),
      dexieWeightSampleLogRepo.list({ farmId: trimmedFarmId, productionUnitId: trimmedPuId }),
      dexieFishStockLogRepo.list({ farmId: trimmedFarmId, productionUnitId: trimmedPuId }),
      dexieLotRepo.list({ farmId: trimmedFarmId, productionUnitId: trimmedPuId })
    ])

    await Promise.all([
      dexieFeedLogRepo.deleteMany(feedLogs.map((row) => row.id)),
      dexieHarvestLogRepo.deleteMany(harvestLogs.map((row) => row.id)),
      dexieLotCostEventRepo.deleteMany(costEvents.map((row) => row.id)),
      dexieMortalityLogRepo.deleteMany(mortalityLogs.map((row) => row.id)),
      dexieWeightSampleLogRepo.deleteMany(weightSampleLogs.map((row) => row.id)),
      dexieFishStockLogRepo.deleteMany(fishStockLogs.map((row) => row.id)),
      ...lots.map((lot) => dexieLotRepo.delete(lot.id))
    ])

    await dexieLotEventRepo.pruneUnreachable(await dexieLotRepo.list())
  } catch (error) {
    console.warn('[entityCachePurge] failed to purge local production unit data', error)
  }
}

/** Purges every locally-cached lot and event-log row for one farm —
 * everything a farm-delete's server-side cascade also destroys.
 * `productionUnits` should be read from the farm object BEFORE it's
 * deleted (the caller already has it in hand); only their id/name are
 * needed, to also clear the legacy production-table/water caches that
 * `handleDeletePU` already cleans up for a single PU. */
export const purgeLocalDataForFarm = async (
  farmId: string,
  productionUnits: ReadonlyArray<{ id: string; name?: string }> = []
): Promise<void> => {
  const trimmed = farmId.trim()
  if (!trimmed) return

  try {
    await ensureOfflineReady()

    const [feedLogs, harvestLogs, costEvents, mortalityLogs, weightSampleLogs, fishStockLogs, lots] = await Promise.all([
      dexieFeedLogRepo.list({ farmId: trimmed }),
      dexieHarvestLogRepo.list({ farmId: trimmed }),
      dexieLotCostEventRepo.list({ farmId: trimmed }),
      dexieMortalityLogRepo.list({ farmId: trimmed }),
      dexieWeightSampleLogRepo.list({ farmId: trimmed }),
      dexieFishStockLogRepo.list({ farmId: trimmed }),
      dexieLotRepo.list({ farmId: trimmed })
    ])

    await Promise.all([
      dexieFeedLogRepo.deleteMany(feedLogs.map((row) => row.id)),
      dexieHarvestLogRepo.deleteMany(harvestLogs.map((row) => row.id)),
      dexieLotCostEventRepo.deleteMany(costEvents.map((row) => row.id)),
      dexieMortalityLogRepo.deleteMany(mortalityLogs.map((row) => row.id)),
      dexieWeightSampleLogRepo.deleteMany(weightSampleLogs.map((row) => row.id)),
      dexieFishStockLogRepo.deleteMany(fishStockLogs.map((row) => row.id)),
      ...lots.map((lot) => dexieLotRepo.delete(lot.id))
    ])

    await dexieLotEventRepo.pruneUnreachable(await dexieLotRepo.list())

    productionUnits.forEach((pu) => {
      deleteProductionUnitTableDataset({ farmId: trimmed, productionUnitId: pu.id, productionUnitName: pu.name })
      deleteWaterTableRows({ farmId: trimmed, productionUnitId: pu.id, productionUnitName: pu.name })
    })
  } catch (error) {
    console.warn('[entityCachePurge] failed to purge local farm data', error)
  }
}
